import {
    createError,
    MarketResearchSearchCriteria,
    RelodeApiError,
} from "@relode/core";
import {
    convertMarketResearchCompensationResponse,
    convertMarketResearchShareResponse,
    MarketResearchRequest,
    convertMarketResearchDiversityResponse,
    convertMarketResearchSupplyDemandResponse,
    convertMarketResearchSummaryResponse,
} from "@relode/core/backend/marketResearch";

import { convertJobManagerContactResponse } from "../../../shared/backend/converters";
import { marketResearch } from "../../../shared/backend/controllers";
import {
    ClientMarketResearchActionTypes,
    creators,
} from "./clientMarketResearchActionTypes";
import { ClientMarketResearchState } from "./clientMarketResearchState";

export const useClientMarketResearchActions = (
    state: ClientMarketResearchState,
    dispatch: React.Dispatch<ClientMarketResearchActionTypes>
) => {
    const updateMarketResearch = (criteria: MarketResearchSearchCriteria) => {
        dispatch(creators.updateMarketResearchSearchCriteria(criteria));
    };

    const loadMarketResearch = async () => {
        try {
            dispatch(creators.loadMarketResearch(state.searchCriteria));

            const summary = loadMarketResearchSummary(state.searchCriteria);

            const compensation = loadMarketResearchCompensation(
                state.searchCriteria
            );

            const supplyDemand = loadMarketResearchSupplyDemand(
                state.searchCriteria
            );

            const diversity = loadMarketResearchDiversity(state.searchCriteria);

            const share = loadMarketResearchShare(state.searchCriteria);

            const jobManager = loadMarketResearchJobManager();

            await Promise.all([
                summary,
                compensation,
                supplyDemand,
                diversity,
                share,
                jobManager,
            ]);

            dispatch(creators.loadMarketResearchSuccess());
        } catch (err) {
            if (err instanceof RelodeApiError) {
                // this function relies on its inner functions to perform necessary error handling
                if (err.isAbort() || err.isClientError()) return;
            }
            dispatch(creators.loadMarketResearchFailure());
        }
    };

    const loadMarketResearchCompensation = async (
        criteria: MarketResearchSearchCriteria
    ) => {
        dispatch(creators.loadMarketResearchCompensation());
        try {
            const request: MarketResearchRequest = {
                jobName: criteria.jobName,
                locationId: criteria.locationId,
            };

            const response =
                await marketResearch.marketResearchService.getCompensation(
                    request
                );
            const data = convertMarketResearchCompensationResponse(response);
            dispatch(creators.loadMarketResearchCompensationSuccess(data));
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                // JLC: We don't want to crash the app when market research fails for any API error
                dispatch(creators.loadMarketResearchCompensationFailure(err));
                return;
            }
            throw createError(
                err,
                "client.clientMarketResearchActions.loadMarketResearchCompensation"
            );
        }
    };

    const loadMarketResearchSupplyDemand = async (
        criteria: MarketResearchSearchCriteria
    ) => {
        dispatch(creators.loadMarketResearchSupplyDemand());
        try {
            const request: MarketResearchRequest = {
                jobName: criteria.jobName,
                locationId: criteria.locationId,
            };

            const response =
                await marketResearch.marketResearchService.getSupplyDemand(
                    request
                );

            const data = convertMarketResearchSupplyDemandResponse(response);

            dispatch(creators.loadMarketResearchSupplyDemandSuccess(data));
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                // JLC: We don't want to crash the app when market research fails for any API error
                dispatch(creators.loadMarketResearchSupplyDemandFailure(err));
                return;
            }
            throw createError(
                err,
                "client.clientMarketResearchActions.loadMarketResearchSupplyDemand"
            );
        }
    };

    const loadMarketResearchDiversity = async (
        criteria: MarketResearchSearchCriteria
    ) => {
        dispatch(creators.loadMarketResearchDiversity());
        try {
            const request: MarketResearchRequest = {
                jobName: criteria.jobName,
                locationId: criteria.locationId,
            };

            const response =
                await marketResearch.marketResearchService.getDiversity(
                    request
                );

            const data = convertMarketResearchDiversityResponse(response);
            dispatch(creators.loadMarketResearchDiversitySuccess(data));
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                // JLC: We don't want to crash the app when market research fails for any API error
                dispatch(creators.loadMarketResearchDiversityFailure(err));
                return;
            }
            throw createError(
                err,
                "client.clientMarketResearchActions.loadMarketResearchDiversity"
            );
        }
    };
    const loadMarketResearchShare = async (
        criteria: MarketResearchSearchCriteria
    ) => {
        dispatch(creators.loadMarketResearchShare());
        try {
            const request: MarketResearchRequest = {
                jobName: criteria.jobName,
                locationId: criteria.locationId,
            };

            const response =
                await marketResearch.marketResearchService.getShare(request);

            const data = convertMarketResearchShareResponse(response);
            dispatch(creators.loadMarketResearchShareSuccess(data));
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                // JLC: We don't want to crash the app when market research fails for any API error
                dispatch(creators.loadMarketResearchShareFailure(err));
                return;
            }
            throw createError(
                err,
                "client.clientMarketResearchActions.loadMarketResearchShare"
            );
        }
    };

    const loadMarketResearchSummary = async (
        criteria: MarketResearchSearchCriteria
    ) => {
        dispatch(creators.loadMarketResearchSummary());
        try {
            const request: MarketResearchRequest = {
                jobName: criteria.jobName,
                locationId: criteria.locationId,
            };

            const response =
                await marketResearch.marketResearchService.getSummary(request);

            const summary = convertMarketResearchSummaryResponse(response);
            
            dispatch(creators.loadMarketResearchSummarySuccess(summary));
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                // JLC: We don't want to crash the app when market research fails for any API error
                dispatch(creators.loadMarketResearchSummaryFailure(err));
                return;
            }
            throw createError(
                err,
                "client.clientMarketResearchActions.loadMarketResearchSummary"
            );
        }
    };

    const loadMarketResearchJobManager = async () => {
        dispatch(creators.loadMarketResearchJobManager());
        try {
            const response =
                await marketResearch.marketResearchService.getJobManagerInfo();

            const jobManagerContact =
                convertJobManagerContactResponse(response);

            dispatch(
                creators.loadMarketResearchJobManagerSuccess(jobManagerContact)
            );
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                // JLC: We don't want to crash the app when market research fails for any API error
                dispatch(creators.loadMarketResearchJobManagerFailure(err));
                return;
            }
            throw createError(
                err,
                "client.clientMarketResearchActions.loadMarketResearchJobManager"
            );
        }
    };

    return {
        loadMarketResearch,
        updateMarketResearch,
    };
};

export type ClientMarketResearchActions = ReturnType<
    typeof useClientMarketResearchActions
>;
